import { Link } from "react-router-dom";
import homeFallbackContent from "../lib/fallbackContent/home";
import { mergeCmsSection } from "../lib/mergeCmsSection";

/**
 * Programs Overview — matches design:
 * cream section, yellow wave top, centered header + CTA,
 * 8 items as separate white text + number cards (mirrored columns).
 */
export default function ProgramsOverview({ data }) {
  const section = mergeCmsSection(data, homeFallbackContent.programs_overview);
  const items = Array.isArray(section.items) ? section.items : [];
  const ctaLink = section.ctaLink || "/what-we-do";
  const ctaText = section.ctaText || "Explore All Programs";

  return (
    <section
      className="relative z-[2] -mt-px bg-[#FAF8F3]"
      aria-labelledby="programs-overview-heading"
    >
      <div className="px-4 pb-16 pt-4 sm:px-6 sm:pb-20 sm:pt-6 lg:px-10 lg:pb-24 lg:pt-8">
        <div className="relative mx-auto max-w-6xl pt-2 sm:pt-3 lg:pt-4">
        {/* Centered intro */}
        <div className="mx-auto max-w-2xl text-center">
          <h2
            id="programs-overview-heading"
            className="text-[28px] font-bold tracking-tight text-[#183B56] sm:text-[34px] lg:text-[40px]"
          >
            {section.title || "Programs Overview"}
          </h2>
          <p className="mt-3 text-[20px] font-bold leading-snug text-[#111827] sm:mt-4 sm:text-[24px] lg:text-[28px]">
            {section.subtitle || "Building Engineers. Strengthening Industries."}
          </p>
          <p className="mx-auto mt-3 max-w-lg text-[14px] leading-relaxed text-slate-500 sm:text-[15px]">
            {section.description ||
              "Our initiatives are designed to support engineers throughout their professional journey."}
          </p>
          <Link
            to={ctaLink}
            className="mt-6 inline-flex items-center gap-2 rounded-lg bg-[#1565D8] px-5 py-2.5 text-[14px] font-semibold text-white transition hover:bg-[#1256c0] sm:mt-7 sm:px-6 sm:py-3 sm:text-[15px]"
          >
            {ctaText}
            <span aria-hidden>→</span>
          </Link>
        </div>

        {/* Program rows — 2 columns, mirrored pairs */}
        <div className="mt-12 grid gap-y-5 sm:mt-14 sm:gap-y-6 lg:grid-cols-2 lg:gap-x-14 xl:gap-x-20">
          {items.map((item, index) => {
            const number = item.number || String(index + 1);
            const isRightColumn = index % 2 === 1;

            const numberCard = (
              <div className="relative flex aspect-square h-auto w-auto shrink-0 self-stretch items-center justify-center overflow-hidden rounded-none bg-white shadow-[0_8px_24px_-8px_rgba(15,23,42,0.2)]">
                <span className="relative z-[1] text-[34px] font-bold leading-none text-[#1f2937] sm:text-[40px]">
                  {number}
                </span>
                {/* Concentric arcs clipped at the corner facing the text card */}
                <span
                  className={[
                    "pointer-events-none absolute z-0 block rounded-full border-[2.5px] border-[#F5C542]/70",
                    isRightColumn
                      ? "-bottom-7 -left-7 h-16 w-16 sm:-bottom-8 sm:-left-8 sm:h-[72px] sm:w-[72px]"
                      : "-bottom-7 -right-7 h-16 w-16 sm:-bottom-8 sm:-right-8 sm:h-[72px] sm:w-[72px]",
                  ].join(" ")}
                  aria-hidden
                />
                <span
                  className={[
                    "pointer-events-none absolute z-0 block rounded-full border-[2.5px] border-[#F5C542]/70",
                    isRightColumn
                      ? "-bottom-5 -left-5 h-12 w-12 sm:-bottom-6 sm:-left-6 sm:h-14 sm:w-14"
                      : "-bottom-5 -right-5 h-12 w-12 sm:-bottom-6 sm:-right-6 sm:h-14 sm:w-14",
                  ].join(" ")}
                  aria-hidden
                />
              </div>
            );

            const textCard = (
              <div className="min-w-0 flex-1 rounded-none bg-white px-4 py-4 text-left shadow-[0_8px_24px_-8px_rgba(15,23,42,0.18)] sm:px-5 sm:py-5">
                <h3 className="text-[15px] font-bold leading-snug text-[#111827] sm:text-[16px]">
                  {item.title}
                </h3>
                <p className="mt-1.5 text-[13px] leading-relaxed text-slate-500 sm:text-[14px]">
                  {item.description}
                </p>
              </div>
            );

            return (
              <article
                key={`${item.title}-${index}`}
                className="flex items-stretch gap-3 sm:gap-3.5"
              >
                {isRightColumn ? (
                  <>
                    {numberCard}
                    {textCard}
                  </>
                ) : (
                  <>
                    {textCard}
                    {numberCard}
                  </>
                )}
              </article>
            );
          })}
        </div>
      </div>
      </div>
    </section>
  );
}
